import React, { useEffect } from 'react';
import { Row, Col } from 'react-bootstrap';
import { UseFormRegister, UseFormWatch, UseFormSetValue, FieldErrors } from 'react-hook-form';
import { TotalsDisplaySection } from './TotalsDisplaySection';
import { blobURL } from '../../common/StringUtils';
import { LOCALES } from '../../common/constants';
import { getItem } from '../helper/localstorage.helper';

const expressDe = `${blobURL}/express-logo-de.png`;
const expressEng = `${blobURL}/express-logo-eng.png`;

interface ExpressPaymentSectionProps {
  // Customer & selection
  selectedCustomer: any;
  selectedFreelancersForInvoice: any[];
  
  // Form
  register: UseFormRegister<any>;
  watch: UseFormWatch<any>;
  setValue: UseFormSetValue<any>;
  errors: FieldErrors;
  
  // Express payment state
  isExpressCheckboxChecked: boolean;
  isEditModeInvoice: boolean;
  
  // Express payment handlers
  handleExpressPaymentCheckbox: (checked: boolean) => void;
  handleExpressPaymentUncheckRequest: () => void;
  handleExpressPaymentReadMore: (e: React.MouseEvent<HTMLAnchorElement>) => void;
  isExpressPaymentAvailable: () => boolean;
  getExpressPaymentMessage: () => string;
  
  // Totals data
  isCalculateInvoiceLoading: boolean;
  calculatedSubAmount: any;
  currency: string;
  language: string;
  vat: number;
  vatAmount: number;
  isCalculationLoading: boolean;
  handleShowCalculationBreakdown: (e: React.MouseEvent<HTMLAnchorElement>) => void;
  
  // Translation
  t: (key: string) => string;
}

const ExpressPaymentSection: React.FC<ExpressPaymentSectionProps> = ({
  selectedCustomer,
  selectedFreelancersForInvoice,
  register,
  watch,
  setValue,
  errors,
  isExpressCheckboxChecked,
  isEditModeInvoice,
  handleExpressPaymentCheckbox,
  handleExpressPaymentUncheckRequest,
  handleExpressPaymentReadMore,
  isExpressPaymentAvailable,
  getExpressPaymentMessage,
  isCalculateInvoiceLoading,
  calculatedSubAmount,
  currency,
  language,
  vat,
  vatAmount,
  isCalculationLoading,
  handleShowCalculationBreakdown,
  t,
}) => {
  // Get form value for expressPayout
  const expressPayoutValue = watch('expressPayout');

  // Show the "express payment not available" warning only after the user
  // actually tries to select the checkbox (keeps the UI clean otherwise).
  const [showUnavailableWarning, setShowUnavailableWarning] = React.useState(false);
  // Drives the exit animation: kept mounted while it plays out, then removed.
  const [isWarningClosing, setIsWarningClosing] = React.useState(false);

  // Reveal the warning (cancelling any in-progress close animation).
  const revealUnavailableWarning = () => {
    setIsWarningClosing(false);
    setShowUnavailableWarning(true);
  };

  const isLoginWithBankId: boolean = (() => {
    try {
      return JSON.parse(getItem('user') as string)?.isLoginWithBankId || false;
    } catch {
      return false;
    }
  })();

  // Debug: Check if express payment is available
  const isAvailable = isExpressPaymentAvailable();
 
  // Automatically uncheck checkbox if amount exceeds 60,000 SEK limit.
  // Skipped in edit mode — there we just SHOW the value saved on the invoice
  // (the checkbox is read-only), so the fetched Express Payment isn't cleared
  // before the calculation has loaded.
  useEffect(() => {
    if (isEditModeInvoice) return;
    const amountIncludingVat = calculatedSubAmount?.response?.totalCalculation?.amountIncludingVat || 0;

    // If checkbox is checked and amount exceeds limit, uncheck it
    if (expressPayoutValue && amountIncludingVat > 60000) {
      setValue('expressPayout', false);
      handleExpressPaymentCheckbox(false);
    }
  }, [isEditModeInvoice, calculatedSubAmount, expressPayoutValue, setValue, handleExpressPaymentCheckbox]);

  // Automatically uncheck the checkbox if express payment stops being available
  // (e.g. the user switches from a business customer to a private one).
  // Skipped in edit mode for the same reason as above.
  useEffect(() => {
    if (isEditModeInvoice) return;
    if (expressPayoutValue && !isAvailable) {
      setValue('expressPayout', false);
      handleExpressPaymentCheckbox(false);
    }
  }, [isEditModeInvoice, isAvailable, expressPayoutValue, setValue, handleExpressPaymentCheckbox]);

  // Hide the "not available" warning again once express payment becomes
  // available, so it only ever reappears on a fresh click attempt.
  useEffect(() => {
    if (isAvailable) {
      setShowUnavailableWarning(false);
      setIsWarningClosing(false);
    }
  }, [isAvailable]);

  // Auto-dismiss the "not available" warning after 5 seconds by starting the
  // exit animation.
  useEffect(() => {
    if (!showUnavailableWarning) {
      return;
    }
    const timer = setTimeout(() => setIsWarningClosing(true), 5000);
    return () => clearTimeout(timer);
  }, [showUnavailableWarning]);

  // Once the exit animation has played, unmount the warning.
  useEffect(() => {
    if (!isWarningClosing) {
      return;
    }
    const timer = setTimeout(() => {
      setShowUnavailableWarning(false);
      setIsWarningClosing(false);
    }, 350);
    return () => clearTimeout(timer);
  }, [isWarningClosing]);
  
  // Only show if freelancers are selected
  if (!selectedFreelancersForInvoice || selectedFreelancersForInvoice.length === 0) {
    return null;
  }

  return (
    <div className="express-payments-section">
      <Row className="express-payment-wrapper">
        {/* Left side: Express Payment Content */}
        {selectedCustomer?.country === 'Sweden' ? (
          <Col xs={12} md={12} lg={12} xl={7} xxl={8} className="express-payments-content mb-3 mb-xl-0">
            {/* Icon + label header — sits above the "Do you want…" line */}
            <div className="iw-express-head">
              <span className="iw-express-ic" aria-hidden="true">
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2" /></svg>
              </span>
              <h4 className="iw-express-title">{t('EXPRESS_PAYMENT') || 'Express Payment'}</h4>
            </div>
            <h6 className="text-dark mb-3 mt-0 read">
              {t('Do you want Express payment on the same day?')}
              
              <a
                className="cursor-pointer how_calculate_link font-weight-bold h6 ml-1"
                onClick={handleExpressPaymentReadMore}
                style={{ textDecoration: 'underline', cursor: 'pointer' }}
              >
                {t('Read more')}
              </a>
            </h6>
            
            {/* Checkbox - Disabled when BankID not used or express payment unavailable */}
            {/* onClick on the wrapper captures the attempt even though the
                disabled checkbox itself won't fire an onChange event. */}
            <div
              className="d-flex align-items-center mb-3"
              onClick={() => {
                // Reveal the relevant warning whenever express payment can't be
                // used (not logged in with BankID, or otherwise unavailable).
                if (!isExpressPaymentAvailable()) {
                  revealUnavailableWarning();
                }
              }}
            >
              <input
                type="checkbox"
                id="express-payments"
                {...register('expressPayout', {
                  onChange: (e: React.ChangeEvent<HTMLInputElement>) => {
                    // Edit mode → read-only. We must NOT use the native `disabled`
                    // attribute here: react-hook-form treats a disabled registered
                    // input's value as undefined, which wipes the fetched
                    // Express Payment (setValue is ignored) so it never shows as
                    // checked. Instead we keep the field enabled to RHF and block
                    // interaction, so the fetched value stays and displays.
                    if (isEditModeInvoice) return;
                    // Keep the checkbox enabled: if express payment isn't
                    // available, block the selection and surface the warning
                    // instead of letting it get checked.
                    if (!isExpressPaymentAvailable()) {
                      revealUnavailableWarning();
                      setValue('expressPayout', false);
                      return;
                    }
                    if (e.target.checked) {
                      // Turning ON → confirm modal (Accept enables / Reject cancels).
                      handleExpressPaymentCheckbox(true);
                    } else {
                      // Turning OFF → open the confirm modal first; the checkbox
                      // stays checked until Accept (keep on) / Reject (turn off).
                      handleExpressPaymentUncheckRequest();
                    }
                  }
                })}
                className="custom-checkbox"
                // Read-only in edit mode without the native `disabled` (see onChange).
                onClick={(e) => { if (isEditModeInvoice) e.preventDefault(); }}
                style={isEditModeInvoice ? { pointerEvents: 'none', cursor: 'not-allowed' } : undefined}
                aria-disabled={isEditModeInvoice || undefined}
                checked={expressPayoutValue || false}
              />
              <label htmlFor="express-payments" className="mb-0 ml-2">
                {/* Themed (violet) Express Payment mark — replaces the raster
                    brand logo so it matches the invoice-tool theme. Clean icon
                    tile + gradient wordmark (no boxy pill). */}
                <span
                  className="express-logo express-logo--themed"
                  role="img"
                  aria-label="Express Payment"
                  style={{ display: 'inline-flex', alignItems: 'center', gap: '11px' }}
                >
                  <span
                    style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      flexShrink: 0,
                    }}
                  >
                    <svg width="30" height="30" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                      <defs>
                        <linearGradient id="expPlaneThemeGrad" x1="2" y1="3" x2="21" y2="21" gradientUnits="userSpaceOnUse">
                          <stop stopColor="#a855f7" />
                          <stop offset="1" stopColor="#7c3aed" />
                        </linearGradient>
                      </defs>
                      <path d="M21.2 3.1L2.9 10.4c-.8.3-.8 1.5.1 1.7l6.3 1.7 1.7 6.3c.2.9 1.4.9 1.7.1L21.9 4c.3-.7-.4-1.4-1.1-1.1z" fill="url(#expPlaneThemeGrad)" />
                      <path d="M9.4 13.7l3.1-3.1" stroke="#ffffff" strokeWidth="1.5" strokeLinecap="round" opacity="0.9" />
                    </svg>
                  </span>
                  <span style={{ display: 'inline-flex', flexDirection: 'column', lineHeight: 1.05, fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
                    <span
                      style={{
                        fontSize: '16px',
                        fontWeight: 800,
                        letterSpacing: '0.06em',
                        background: 'linear-gradient(135deg, #a855f7 0%, #6d28d9 100%)',
                        WebkitBackgroundClip: 'text',
                        backgroundClip: 'text',
                        WebkitTextFillColor: 'transparent',
                        color: '#7c3aed',
                      }}
                    >
                      EXPRESS
                    </span>
                    <span style={{ fontSize: '9.5px', fontWeight: 700, letterSpacing: '0.34em', color: '#a78bce', marginTop: '3px', textTransform: 'uppercase' }}>Payment</span>
                  </span>
                </span>
              </label>
            </div>

            {/* Warning: not logged in with BankID - shown only on click attempt */}
            {!isLoginWithBankId && showUnavailableWarning && (
              <div className={`mt-0 mb-3 express-payment-warning${isWarningClosing ? ' is-closing' : ''}`}>
                <div 
                  className="alert alert-warning d-flex align-items-center" 
                  style={{ 
                    backgroundColor: '#fff3cd', 
                    border: '1px solid #ffeaa7', 
                    borderRadius: '8px',
                    padding: '12px 16px',
                    fontSize: '14px',
                    fontWeight: '500'
                  }}
                >
                  <i 
                    className="fas fa-exclamation-triangle mr-2" 
                    style={{ color: '#856404', fontSize: '16px' }}
                  ></i>
                  <span style={{ color: '#856404' }}>
                    <strong>{t('Action Required:')}</strong> {t('Please log in with BankID to use Express Payment.')}
                  </span>
                </div>
              </div>
            )}

            {/* Warning Message - Show only when the user tries to select
                Express Payment while it is not available */}
            {isLoginWithBankId && !isExpressPaymentAvailable() && showUnavailableWarning && (
              <div className={`mt-0 mb-3 express-payment-warning${isWarningClosing ? ' is-closing' : ''}`}>
                <div 
                  className="alert alert-warning d-flex align-items-center" 
                  style={{ 
                    backgroundColor: '#fff3cd', 
                    border: '1px solid #ffeaa7', 
                    borderRadius: '8px',
                    padding: '12px 16px',
                    fontSize: '14px',
                    fontWeight: '500'
                  }}
                >
                  <i 
                    className="fas fa-exclamation-triangle mr-2" 
                    style={{ color: '#856404', fontSize: '16px' }}
                  ></i>
                  <span style={{ color: '#856404' }}>
                    <strong>{t('Action Required:')}</strong> {getExpressPaymentMessage()}
                  </span>
                </div>
              </div>
            )}

            {errors.expressPayout && (
              <div className="error-message">{errors.expressPayout.message?.toString()}</div>
            )}
          </Col>
        ) : (
          <Col xs={12} md={12} lg={12} xl={7} xxl={8} className="express-payments-content mb-3 mb-xl-0"></Col>
        )}

        {/* Right side: Totals Section */}
        <TotalsDisplaySection
          isCalculateInvoiceLoading={isCalculateInvoiceLoading}
          calculatedSubAmount={calculatedSubAmount}
          currency={currency}
          language={language}
          vat={vat}
          vatAmount={vatAmount}
          isCalculationLoading={isCalculationLoading}
          handleShowCalculationBreakdown={handleShowCalculationBreakdown}
          t={t}
        />
      </Row>
    </div>
  );
};

export default ExpressPaymentSection;
